import {
  DemandAiReviewResultItemDto,
  DemandAiReviewResultItemGroupDto,
} from '#services/api';
import { Button, Popover, Tooltip } from '@bedrock/components';
import { PopoverProps } from '@bedrock/components/lib/Popover';
import {
  AlertCircle,
  AlertCircleFilled,
  ChevronDown,
} from '@bedrock/icons-react';
import { useMemoizedFn } from 'ahooks';
import classNames from 'classnames';
import React, { FC, useMemo, useRef } from 'react';
import FileExceptionDetailModal, {
  FileExceptionDetailModalOpenParams,
  FileExceptionDetailModalRef,
} from './FileExceptionDetailModal';
import './index.less';

export interface AuditExceptionProps {
  data?: DemandAiReviewResultItemDto;
  detailModalOpenProps?: FileExceptionDetailModalOpenParams;
  popover?: true | PopoverProps;
  showGroups?: boolean;
}

const AuditException: FC<AuditExceptionProps> = (props) => {
  const { data, detailModalOpenProps, popover, showGroups } = props;

  const fileExceptionDetailModalRef = useRef<FileExceptionDetailModalRef>(null);

  const openFileExceptionDetailModal = useMemoizedFn(
    (group?: DemandAiReviewResultItemGroupDto) => {
      fileExceptionDetailModalRef.current?.open?.({
        modalProps: {
          title: `异常明细${group?.content ? ` - ${group?.content}` : ''}`,
          ...detailModalOpenProps?.modalProps,
        },
        detailProps: { data: group, ...detailModalOpenProps?.detailProps },
      });
    }
  );

  const titleText = useMemo(() => {
    if (data?.groups?.length) {
      return `异常文件 (${data?.groups.length})`;
    }
    if (data?.error_message) {
      return data?.error_message;
    }
    return null;
  }, [data?.error_message, data?.groups?.length]);

  const details = useMemo(() => {
    if (showGroups && data?.groups?.length) {
      return data?.groups.map((group, index) => (
        <div key={index} className="audit-exception-file">
          <Tooltip title={group.content}>
            <span className="file-name">{group.content}</span>
          </Tooltip>
          <span className="exception-count">
            {`异常项 (${group.details?.length ?? 0})`}
          </span>
          <Button
            type="link"
            onClick={() => openFileExceptionDetailModal(group)}
          >
            查看
          </Button>
        </div>
      ));
    }
    return null;
  }, [showGroups, data?.groups]);

  const popoverProps = useMemo(() => {
    if (popover === true) {
      return {};
    }
    return popover;
  }, [popover]);

  if (!data) {
    return null;
  }

  if (popoverProps) {
    return (
      <Popover
        placement="topRight"
        trigger="click"
        {...popoverProps}
        overlayClassName={classNames(
          'audit-exception-popover',
          popoverProps?.overlayClassName
        )}
        content={<AuditException {...props} popover={undefined} showGroups />}
      >
        <span className="audit-exception-popover-entry">
          <AlertCircle />
          <span className="text-sm">异常</span>
          <ChevronDown />
        </span>
      </Popover>
    );
  }

  return (
    <>
      <div className="audit-exception">
        <div className="audit-exception-title">
          <AlertCircleFilled size={16} />
          <span>{titleText}</span>
        </div>
        {details && <div className="audit-exception-details">{details}</div>}
      </div>
      <FileExceptionDetailModal ref={fileExceptionDetailModalRef} />
    </>
  );
};

export default AuditException;
